import { PDFArray, PDFDict, PDFHexString, PDFName, PDFNumber, PDFRawStream, PDFString, decodePDFRawStream } from "pdf-lib";

/**
 * Reads the stored bytes of a PDF picture whatever its chain of filters is: ASCII85 or hex text
 * wrapped around a JPEG, Flate or LZW or run-length around raw samples, with or without a PNG/TIFF
 * predictor, and palette (Indexed) or 1/2/4-bit samples. The library's own decoder ignores
 * predictors, which is why lossless screenshots used to be skipped.
 */

const BYTE_FILTERS = new Set(["FlateDecode", "LZWDecode", "ASCII85Decode", "ASCIIHexDecode", "RunLengthDecode", "Fl", "LZW", "A85", "AHx", "RL"]);
const LONG: Record<string, string> = { Fl: "FlateDecode", LZW: "LZWDecode", A85: "ASCII85Decode", AHx: "ASCIIHexDecode", RL: "RunLengthDecode", DCT: "DCTDecode", CCF: "CCITTFaxDecode" };

export interface FilterChain {
  names: string[];
  parms: (PDFDict | undefined)[];
}

export function filterChain(dict: PDFDict): FilterChain {
  const f = dict.lookup(PDFName.of("Filter"));
  const p = dict.lookup(PDFName.of("DecodeParms")) ?? dict.lookup(PDFName.of("DP"));
  const names: string[] = [];
  const parms: (PDFDict | undefined)[] = [];
  const norm = (n: string) => LONG[n] ?? n;
  if (f instanceof PDFName) {
    names.push(norm(f.decodeText()));
    parms.push(p instanceof PDFDict ? p : p instanceof PDFArray ? p.lookupMaybe(0, PDFDict) : undefined);
  } else if (f instanceof PDFArray) {
    for (let i = 0; i < f.size(); i++) {
      const n = f.lookup(i);
      names.push(n instanceof PDFName ? norm(n.decodeText()) : "?");
      parms.push(p instanceof PDFArray ? p.lookupMaybe(i, PDFDict) : i === 0 && p instanceof PDFDict ? p : undefined);
    }
  }
  return { names, parms };
}

/** "jpeg" when the last step is DCT, "raw" when only byte-level filters are used, otherwise null (JPX, CCITT, JBIG2, unknown). */
export function chainKind(chain: FilterChain): "jpeg" | "raw" | null {
  if (!chain.names.length) return "raw";
  const last = chain.names[chain.names.length - 1];
  const outer = last === "DCTDecode" ? chain.names.slice(0, -1) : chain.names;
  if (!outer.every((n) => BYTE_FILTERS.has(n))) return null;
  return last === "DCTDecode" ? "jpeg" : "raw";
}

const num = (d: PDFDict | undefined, key: string, def: number) => {
  const v = d?.lookup(PDFName.of(key));
  return v instanceof PDFNumber ? v.asNumber() : def;
};

function paeth(a: number, b: number, c: number) {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  return pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
}

/** Undoes a PNG (10-15) or TIFF (2) predictor. */
export function undoPredictor(data: Uint8Array, parms: PDFDict | undefined): Uint8Array {
  const predictor = num(parms, "Predictor", 1);
  if (predictor <= 1) return data;
  const colors = num(parms, "Colors", 1);
  const bpc = num(parms, "BitsPerComponent", 8);
  const columns = num(parms, "Columns", 1);
  const bpp = Math.max(1, Math.ceil((colors * bpc) / 8));
  const rowBytes = Math.ceil((colors * bpc * columns) / 8);
  if (predictor === 2) {
    if (bpc !== 8) return data;
    const out = data.slice();
    for (let r = 0; r * rowBytes < out.length; r++) {
      const o = r * rowBytes;
      for (let i = bpp; i < rowBytes && o + i < out.length; i++) out[o + i] = (out[o + i] + out[o + i - bpp]) & 255;
    }
    return out;
  }
  const rows = Math.floor(data.length / (rowBytes + 1));
  const out = new Uint8Array(rows * rowBytes);
  for (let r = 0; r < rows; r++) {
    const type = data[r * (rowBytes + 1)];
    const src = r * (rowBytes + 1) + 1;
    const dst = r * rowBytes;
    const up = dst - rowBytes;
    for (let i = 0; i < rowBytes; i++) {
      const x = data[src + i];
      const a = i >= bpp ? out[dst + i - bpp] : 0;
      const b = r > 0 ? out[up + i] : 0;
      const c = r > 0 && i >= bpp ? out[up + i - bpp] : 0;
      out[dst + i] = type === 0 ? x : type === 1 ? x + a : type === 2 ? x + b : type === 3 ? x + ((a + b) >> 1) : x + paeth(a, b, c);
    }
  }
  return out;
}

const single = (stream: PDFRawStream, name: string, parms: PDFDict | undefined, bytes: Uint8Array) => {
  const dict = stream.dict.context.obj({ Filter: name }) as PDFDict;
  if (parms) dict.set(PDFName.of("DecodeParms"), parms);
  return decodePDFRawStream(PDFRawStream.of(dict, bytes)).decode();
};

/**
 * The picture's bytes with every byte-level filter undone: the JPEG file for a DCT picture, the packed
 * samples for a raw one. Throws when a filter cannot be undone.
 */
export function decodeStoredBytes(stream: PDFRawStream, chain = filterChain(stream.dict)): Uint8Array {
  let bytes: Uint8Array = stream.getContents();
  for (let i = 0; i < chain.names.length; i++) {
    const name = chain.names[i];
    if (name === "DCTDecode") break;
    bytes = single(stream, name, undefined, bytes);
    if (name === "FlateDecode" || name === "LZWDecode") bytes = undoPredictor(bytes, chain.parms[i]);
  }
  return bytes;
}

/** Palette lookup table as RGB triplets, or null when the colour space is not a palette of RGB/grey. */
export function readPalette(csObj: unknown, resolveBase: (o: unknown) => 1 | 3 | null): { palette: Uint8Array; hival: number } | null {
  if (!(csObj instanceof PDFArray) || csObj.size() !== 4) return null;
  const head = csObj.lookup(0);
  if (!(head instanceof PDFName) || !["Indexed", "I"].includes(head.decodeText())) return null;
  const baseComps = resolveBase(csObj.lookup(1));
  const hival = csObj.lookup(2);
  if (!baseComps || !(hival instanceof PDFNumber)) return null;
  const table = csObj.lookup(3);
  let bytes: Uint8Array | null = null;
  if (table instanceof PDFString || table instanceof PDFHexString) bytes = table.asBytes();
  else if (table instanceof PDFRawStream) {
    try {
      bytes = decodeStoredBytes(table);
    } catch {
      bytes = null;
    }
  }
  const n = hival.asNumber() + 1;
  if (!bytes || bytes.length < n * baseComps) return null;
  const palette = new Uint8Array(n * 3);
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < 3; k++) palette[i * 3 + k] = baseComps === 3 ? bytes[i * 3 + k] : bytes[i];
  }
  return { palette, hival: n - 1 };
}

/** Packed samples (1, 2, 4 or 8 bits) → one byte per sample, rows padded to whole bytes in the input. */
export function unpackSamples(data: Uint8Array, width: number, height: number, perPixel: number, bpc: number): Uint8Array {
  if (bpc === 8) return data;
  const rowBytes = Math.ceil((width * perPixel * bpc) / 8);
  const out = new Uint8Array(width * height * perPixel);
  const mask = (1 << bpc) - 1;
  let o = 0;
  for (let r = 0; r < height; r++) {
    for (let i = 0; i < width * perPixel; i++) {
      const bit = i * bpc;
      const byte = data[r * rowBytes + (bit >> 3)] ?? 0;
      out[o++] = (byte >> (8 - bpc - (bit & 7))) & mask;
    }
  }
  return out;
}
